import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { SlidersHorizontal } from 'lucide-react'
import { EDITOR_ICONS, EDITOR_TITLES } from './editable/editorOptions'
import { useLucideIcon, FALLBACK_ICON } from './primitives/lucideRegistry'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'
import { largeurUtile } from '../shell/cornerDock';

/**
 * Livrée du picto : celle des autres boutons de la barre du bloc. Une cellule de
 * gabarit (colonne, grille) a un chrome différent — pastilles blanches posées
 * SUR le bloc — et passe la sienne par `triggerClassName`.
 */
const BLOCK_BAR_TRIGGER = 'p-1 rounded text-gray-500 hover:text-teal-700 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1'

/** Le même picto dans le chrome d'une cellule de gabarit. */
export const CELL_CHROME_TRIGGER = 'pointer-events-auto flex items-center justify-center w-6 h-6 rounded-md bg-white/90 border border-gray-200 text-gray-400 hover:text-teal-700 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500'

// Menu « Options avancées » : largeur, et hauteur estimée d'une entrée. Elles
// décident du basculement au-dessus du picto quand le bas de la fenêtre est
// trop proche — même calcul que le menu « Ajouter » du canevas.
const SETTINGS_MENU_WIDTH = 268
const SETTINGS_MENU_ITEM_HEIGHT = 36

/**
 * Une entrée du menu. Composant à part parce que `useLucideIcon` est un HOOK :
 * il ne peut pas être appelé dans le `.map()` du menu.
 */
const BlockSettingMenuItem = ({ decl, onPick }) => {
  const type = decl.type ?? decl.editorType
  const Icon = useLucideIcon(decl.icon ?? EDITOR_ICONS[type] ?? 'Settings')
  const Rendered = Icon === null ? FALLBACK_ICON : Icon
  const label = decl.label ?? EDITOR_TITLES[type] ?? decl.field

  return (
    <button
      type="button"
      role="menuitem"
      onClick={() => onPick(decl)}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-teal-50 focus-visible:bg-teal-50 focus-visible:outline-none"
    >
      {/* Undefined = une icône paresseuse encore en vol : l'entrée garde sa taille. */}
      {Rendered
        ? <Rendered size={14} className="shrink-0 text-slate-400" aria-hidden="true" />
        : <span className="shrink-0" style={{ width: 14, height: 14 }} />}
      <span className="truncate">{label}</span>
    </button>
  )
}

/**
 * « Options avancées » — les réglages qui gouvernent LE BLOC ENTIER (un thème,
 * un mode anti-robot, les destinataires d'un formulaire), déclarés par la déf
 * dans `settings:` et repliés derrière un picto de la barre du bloc.
 *
 * POURQUOI ICI et non dans un rail de ligne : un rail est l'affordance de ce
 * qu'on ÉDITE — le contenu, à l'endroit où il s'affiche. Un réglage de bloc ne
 * s'affiche nulle part ; posé dans le rendu, il devient une pastille de plus au
 * milieu du formulaire, allumée au survol d'un contenu qui n'est pas le sien.
 * La barre du bloc porte déjà tout ce qui parle DU bloc (IA, monter/descendre,
 * supprimer, visibilité par appareil) : ces réglages y sont chez eux, et repliés
 * pour que la barre ne grandisse pas d'un picto par réglage.
 *
 * Le menu est en PORTAIL, pour la même raison que le panneau « bloc partagé » :
 * une cellule de grille est positionnée par une transformation CSS (donc bloc
 * conteneur d'un `position: fixed`) et découpe en `overflow: hidden`.
 */
export const BlockSettingsMenu = ({ settings, onPick, triggerClassName = BLOCK_BAR_TRIGGER }) => {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const menuId = useId()

  const close = useCallback(({ refocus = true } = {}) => {
    setOpen(false)
    if (refocus) triggerRef.current?.focus()
  }, [])

  // Échap referme et rend le focus au picto — sinon il repart à la racine du
  // document et l'on retraverse la page entière au clavier.
  useEscapeClose(() => close(), open)

  useEffect(() => {
    if (!open) return
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      // Aligné à DROITE du picto : la barre du bloc est elle-même collée au
      // bord droit du bloc, un menu aligné à gauche déborderait de la fenêtre.
      const left = Math.min(
        Math.max(8, rect.right - SETTINGS_MENU_WIDTH),
        Math.max(8, largeurUtile() - SETTINGS_MENU_WIDTH - 8),
      )
      const needed = settings.length * SETTINGS_MENU_ITEM_HEIGHT + 12
      setPos(window.innerHeight - rect.bottom < needed
        ? { left, bottom: window.innerHeight - rect.top + 6 }
        : { left, top: rect.bottom + 6 })
    }
    place()
    // Capture : le défilement du canevas ne bouillonne pas jusqu'à `window`, or
    // c'est LUI qui déplace le déclencheur.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, settings.length])

  useEffect(() => {
    if (!open) return
    // Le menu vit hors du sous-arbre du déclencheur (portail) : les deux
    // comptent comme « dedans », sinon il se fermerait sur ses propres clics.
    const onDocPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDocPointerDown)
    return () => document.removeEventListener('mousedown', onDocPointerDown)
  }, [open])

  // Le menu est une liste de commandes : les flèches y naviguent (WAI-ARIA
  // « menu button »), Tab en sort en le refermant.
  const onMenuKeyDown = (e) => {
    const buttons = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') ?? [])
    const at = buttons.indexOf(document.activeElement)
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const next = e.key === 'ArrowDown' ? at + 1 : at - 1
      buttons[(next + buttons.length) % buttons.length]?.focus()
    } else if (e.key === 'Home') { e.preventDefault(); buttons[0]?.focus() }
    else if (e.key === 'End') { e.preventDefault(); buttons[buttons.length - 1]?.focus() }
    else if (e.key === 'Tab') { close({ refocus: false }) }
  }

  const enterMenu = () => requestAnimationFrame(
    () => menuRef.current?.querySelector('[role="menuitem"]')?.focus(),
  )

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        onKeyDown={(e) => {
          // Flèche bas depuis le déclencheur : ouvre ET entre dans le menu.
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
            enterMenu()
          }
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label="Options avancées du bloc"
        title="Options avancées du bloc"
        className={triggerClassName}
      >
        <SlidersHorizontal size={15} aria-hidden="true" />
      </button>
      {open && createPortal(
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          aria-label="Options avancées du bloc"
          onKeyDown={onMenuKeyDown}
          className="popover-menu"
          // Le menu est monté AVANT d'être placé (il faut son déclencheur pour
          // le mesurer) : masqué, il garde son ordre de tabulation et laisse
          // l'entrée recevoir le focus, sans clignoter en haut à gauche.
          style={{ ...(pos ?? { top: 0, left: 0 }), width: SETTINGS_MENU_WIDTH, visibility: pos ? 'visible' : 'hidden' }}
        >
          {settings.map((decl, i) => (
            <div key={i} className={i > 0 ? 'border-t border-slate-100' : undefined}>
              <BlockSettingMenuItem
                decl={decl}
                onPick={(d) => { close({ refocus: false }); onPick(d, triggerRef.current) }}
              />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  )
}

